import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field } from "@/components/admin/fields";

export const SetsTab = ({ FormModal }) => {
  const [sets, setSets] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => {
    api.get("/admin/sets").then((r) => setSets(r.data)).catch(() => {});
    api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openForm = (s) => {
    setEditing(s || null);
    setForm(s ? { ...s } : { title: "", subtitle: "", product_ids: [], offer_type: "none", coupon_code: "", link: "", active: true });
    setSearch("");
  };

  const toggleProduct = (id) =>
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id],
    }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/sets/${editing.id}`, form);
      else await api.post("/admin/sets", form);
      toast.success(editing ? "Set updated" : "Set created — live on the homepage");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete set "${title}"?`)) return;
    await api.delete(`/admin/sets/${id}`).catch(() => {});
    load();
  };

  const visible = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 40);

  return (
    <div data-testid="sets-tab">
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-zinc-500 max-w-xl">
          Build your own homepage collections — pick products, attach an offer (coupon code or link), and publish.
        </p>
        <button onClick={() => openForm(null)} data-testid="set-add-btn" className="flex items-center gap-2 bg-gold text-white text-[10px] uppercase tracking-[0.3em] px-6 py-3 hover:bg-champagne transition-colors duration-300">
          <Plus size={14} /> Create Set
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sets.map((s) => (
          <div key={s.id} className="border border-zinc-200 p-6" data-testid={`set-card-${s.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-serif text-2xl text-zinc-900">{s.title}</p>
                <p className="text-xs text-zinc-400 mt-1">{s.product_ids.length} products · {s.active ? "Live" : "Hidden"}
                  {s.offer_type === "coupon" && s.coupon_code ? ` · Coupon ${s.coupon_code}` : s.offer_type === "link" ? " · Linked offer" : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openForm(s)} data-testid={`set-edit-${s.id}`} className="p-2 text-zinc-400 hover:text-gold transition-colors duration-200" aria-label="Edit"><Pencil size={15} /></button>
                <button onClick={() => remove(s.id, s.title)} data-testid={`set-delete-${s.id}`} className="p-2 text-zinc-400 hover:text-red-500 transition-colors duration-200" aria-label="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
        {sets.length === 0 && <p className="font-serif italic text-xl text-zinc-400">No sets yet — create your first collection.</p>}
      </div>

      {form && (
        <FormModal title={editing ? "Edit Set" : "New Set"} onClose={() => setForm(null)} testid="set-form-modal">
          <form onSubmit={save} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="set-form-title" />
              <Field label="Subtitle" value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} data-testid="set-form-subtitle" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Offer Type</label>
                <select value={form.offer_type} onChange={(e) => setForm((f) => ({ ...f, offer_type: e.target.value }))} data-testid="set-form-offer-type" className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60">
                  <option value="none">No offer</option>
                  <option value="coupon">Attach coupon code</option>
                  <option value="link">Link to a page</option>
                </select>
              </div>
              {form.offer_type === "coupon" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Coupon</label>
                  <select value={form.coupon_code} onChange={(e) => setForm((f) => ({ ...f, coupon_code: e.target.value }))} data-testid="set-form-coupon" className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60">
                    <option value="">Choose…</option>
                    {coupons.map((c) => <option key={c.id} value={c.code}>{c.code}</option>)}
                  </select>
                </div>
              )}
              {form.offer_type === "link" && (
                <Field label="Link (e.g. /offers)" value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} data-testid="set-form-link" />
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                Products ({form.product_ids.length} selected)
              </label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products…"
                data-testid="set-product-search"
                className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 mb-3 focus:outline-none focus:border-gold/60 transition-colors duration-300"
              />
              <div className="max-h-56 overflow-y-auto border border-zinc-200 divide-y divide-zinc-100" data-testid="set-product-picker">
                {visible.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-600 hover:bg-pink-50/50 cursor-pointer">
                    <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} data-testid={`set-product-${p.id}`} className="accent-[#E80071] w-3.5 h-3.5" />
                    <span className="flex-1 truncate">{p.name}</span>
                    <span className="text-xs text-zinc-400">₹{p.price}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm text-zinc-600">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} data-testid="set-form-active" className="accent-[#E80071] w-4 h-4" />
              Live on homepage
            </label>
            <button type="submit" data-testid="set-form-submit" className="w-full bg-gold text-white text-xs uppercase tracking-[0.3em] py-4 hover:bg-champagne transition-colors duration-300">
              {editing ? "Save Changes" : "Publish Set"}
            </button>
          </form>
        </FormModal>
      )}
    </div>
  );
};
